import React, { useState, useEffect } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts'
import {
  Users,
  CurrencyDollar,
  TrendUp,
  Money
} from 'phosphor-react'
import { getConfig } from '../config/constants'

const config = getConfig()
const API_BASE_URL = config.API_URL

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<any>(null)
  const [timeRange, setTimeRange] = useState('7d')

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('admin_token')
      const res = await fetch(`${API_BASE_URL}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      const data = await res.json()
      setStats(data)
    } catch (e) {
      // fallback: não mostra nada
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo':
        return 'bg-green-100 text-green-800'
      case 'Pendente':
        return 'bg-yellow-100 text-yellow-800'
      case 'Inativo':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatValue = (value: number | string, type: string) => {
    if (typeof value === 'string') {
      return value
    }
    if (type === 'currency') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value)
    }
    if (type === 'percentage') {
      return `${value}%`
    }
    return value.toLocaleString('pt-BR')
  }

  if (loading || !stats) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Cards de estatísticas
  const statsData = [
    { name: 'Usuários Ativos', value: stats.activeUsers, change: 0, icon: Users, color: 'green' },
    { name: 'Assinaturas Ativas', value: stats.activeSubscriptions, change: 0, icon: Money, color: 'green' },
    { name: 'Receita Mensal', value: stats.monthlyRevenue, change: 0, icon: CurrencyDollar, color: 'green' },
    { name: 'Taxa de Conversão', value: stats.conversionRate, change: 0, icon: TrendUp, color: 'red' },
  ]

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Visão geral do sistema</p>
        </div>
        <div className="flex items-center space-x-2">
          <select 
            className="input w-32"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            disabled
          >
            <option value="7d">7 dias</option>
            <option value="30d">30 dias</option>
            <option value="90d">90 dias</option>
            <option value="1y">1 ano</option>
          </select>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsData.map((stat, index) => (
          <div key={index} className="card card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatValue(stat.value, stat.name.includes('Receita') ? 'currency' : stat.name.includes('Taxa') ? 'percentage' : 'number')}
                </p>
              </div>
              <div className={`p-3 rounded-full bg-${stat.color}-100`}>
                <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Usuários Recentes */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Usuários Recentes</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {stats.recentUsers.map((user: any) => (
              <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`badge ${getStatusColor(user.isActive ? 'Ativo' : 'Inativo')}`}>
                    {user.isActive ? 'Ativo' : 'Inativo'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{user.subscriptionStatus === 'active' ? 'Premium' : 'Básico'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
